import React, { FC, ReactElement, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Session, updateHeaderConfig } from 'src/front/api/apiInstance';

import Application from 'src/front/components/Application';

import { StepperRefProps } from 'src/front/components/StepperView';
import ContactInfoProvider, { useClearDispatch } from 'src/front/contexts/ContactInfoContext';
import { SubMenuIds } from 'src/front/routes/paths';
import ClientInformation from './ClientInformation';
import ContactInfoDocumentUpload from './ContactInfoDocumentUpload';
import ContactInformation from './ContactInformation';
import ContactInfoSearch from './ContactInfoSearch';

const ContactInfo: FC = () => {
  const { t } = useTranslation();
  const stepperRef = useRef<StepperRefProps>(null);

  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const clearOldData = useClearDispatch();
  const goToNextStep = (): void => {
    stepperRef.current?.goToNextStep();
  };

  const onRepeat = async (): Promise<void> => {
    const { data, headers } = await Session.clearSessionData({
      menuID: SubMenuIds.CONTACT_INFO,
    });

    if (data.result === 'OK') {
      clearOldData();
      updateHeaderConfig(data.sessionID, headers.authorization);
      stepperRef.current?.goToFirstStep();
    }
  };

  const clearCustomerSearch = async (): Promise<void> => {
    const { data, headers } = await Session.clearSessionData({
      menuID: SubMenuIds.CONTACT_INFO,
    });
    if (data.result === 'OK') {
      clearOldData();
      updateHeaderConfig(data.sessionID, headers.authorization);
    }
  };

  const steps = [
    {
      component: () => (
        <ContactInfoSearch clearCustomerSearch={clearCustomerSearch} goToNextStep={goToNextStep} />
      ),
    },
    {
      component: () => <ClientInformation goToNextStep={goToNextStep} />,
    },
    {
      component: () => <ContactInformation goToNextStep={goToNextStep} />,
    },
    {
      component: () => (
        <ContactInfoDocumentUpload onRepeat={onRepeat} setIsCompleted={setIsCompleted} />
      ),
    },
  ];

  const stepsVertical = useMemo(() => {
    return [
      {
        index: 1,
        text: t('changeFundClientInfo_stepper_clientSearch_text'),
      },
      {
        index: 2,
        text: t('changeContactInfo_stepper_changeClientContactInfo_text'),
        subSteps: [
          {
            index: 1,
            text: t('cardEntry_stepper_customerStep_title'),
          },
          {
            index: 2,
            text: t('changeContactInfo_stepper_changeClientContactInfoStepSub1_text'),
          },
          {
            index: 3,
            text: t('changeContactInfo_stepper_changeClientContactInfoStepSub2_text'),
          },
        ],
      },
    ];
  }, [t]);

  return (
    <Application
      isCompleted={isCompleted}
      stepperRef={stepperRef}
      steps={steps}
      stepsVertical={stepsVertical}
    />
  );
};

export default (): ReactElement => (
  <ContactInfoProvider>
    <ContactInfo />
  </ContactInfoProvider>
);
